import { useState, useEffect, useRef } from 'react';
import { IProduct, addProduct, updateProduct, deleteProduct } from '../../../../libraries/helpdesk/product-management';
import { Language } from '../../../languages/languageHandler';
import ModalComponent from '../../../components/modal';
import { Grid, GridCol, TextInput, Checkbox, Button, Group, NumberInput, Container } from '@mantine/core';
import { ICurrency } from '../../../../libraries/currencies';
import { useStatusOperDialog } from '../../../context/status-oper-dialog';
import { StatusType } from '../../../../libraries/zps';

interface ModalProps {
  mode: string;
  isOpen: boolean;
  hideBtn: VoidFunction;
  lang: Language;
  product: IProduct;
  currency: ICurrency;
  onSuccess?: () => void;
}

const ProductModal = ({ mode, isOpen, hideBtn, lang, product, currency, onSuccess }: ModalProps) => {
  const controller = useRef(new AbortController()).current;

  const [loading, setLoading] = useState(false);
  const [productCode, setProductCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [um, setUm] = useState<string>('');
  const [hasComposition, setHasComposition] = useState<boolean>(false);
  const [hasSn, setHasSn] = useState<boolean>(false);
  const [priceRef, setPriceRef] = useState<number>(0);
  const [lastSupplier, setLastSupplier] = useState<number>(0);
  const [lastPrice, setLastPrice] = useState<number>(0);
  const [ceCertificate, setCeCertificate] = useState<boolean>(false);
  const [cbCertificate, setCbCertificate] = useState<boolean>(false);
  const [averDeliTime, setAverDeliTime] = useState<number>(0);
  const [devType, setDevType] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [cType, setCType] = useState<number>(0);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  useEffect(() => {
    if (product) {
      setProductCode(product.productCode || '');
      setDescription(product.description || '');
      setUm(product.um || '');
      setHasComposition(product.hasComposition || false);
      setHasSn(product.hasSN || false);
      setPriceRef(Number((product.priceRef * currency.factor).toFixed(2)) || 0);
      setLastSupplier(product.lastSupplier || 0);
      setLastPrice(Number((product.lastPrice * currency.factor).toFixed(2)) || 0);
      setCeCertificate(product.ceCertificate || false);
      setCbCertificate(product.cbCertificate || false);
      setAverDeliTime(product.averDeliTime || 0);
      setDevType(product.devType || 0);
      setPrice(Number((product.price * currency.factor).toFixed(2)) || 0);
      setCType(product.ctype || 0);
    } else {
      setProductCode('');
      setDescription('');
      setUm('');
      setHasComposition(false);
      setHasSn(false);
      setPriceRef(0);
      setLastSupplier(0);
      setLastPrice(0);
      setCeCertificate(false);
      setCbCertificate(false);
      setAverDeliTime(0);
      setDevType(0);
      setPrice(0);
      setCType(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, mode]);

  const handleTitle = (currentMode: string) => {
    switch (currentMode) {
      case 'read':
        return `${product?.productCode || ''} - ${product?.description || ''}`;
      case 'new':
        return lang.PRODUCT_ADD_PRODUCT || 'Add Product';
      case 'edit':
        return `${product?.productCode || ''} - ${product?.description || ''}`;
      default:
        return '';
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const productData: IProduct = {
        productCode,
        description,
        um,
        hasComposition,
        hasSN: hasSn,
        priceRef: priceRef / currency.factor,
        lastSupplier,
        lastPrice: lastPrice / currency.factor,
        ceCertificate,
        cbCertificate,
        averDeliTime,
        devType,
        price,
        ctype: cType
      };

      const response =
        mode === 'new' ? await addProduct(productData, controller) : await updateProduct(productData, controller);

      const statusOper = response.data?.statusOper;

      if (!statusOper || statusOper.code !== 0) {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.PRODUCTS_EDIT_ERROR || 'Error saving product',
          statusOper
        });
        return;
      }

      const successMessage =
        mode === 'new'
          ? lang.PRODUCTS_ADD_SUCCESS || 'Created successfully'
          : lang.PRODUCTS_EDIT_SUCCESS || 'Updated successfully';

      showStatusOperDialog({
        type: StatusType.INFO,
        message: successMessage,
        statusOper
      });

      onSuccess?.();

      setTimeout(() => {
        hideBtn();
      }, 500);
    } catch (error) {
      handleApiError(error, lang.PRODUCTS_ADD_ERROR || 'Error saving product');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      const response = await deleteProduct(product.productCode, controller);

      const statusOper = response.data?.statusOper;

      if (!statusOper || statusOper.code !== 0) {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.PRODUCTS_DELETE_ERROR || 'Error deleting product',
          statusOper
        });
        return;
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message: lang.PRODUCTS_DELETE_SUCCESS || 'Deleted successfully',
        statusOper
      });

      onSuccess?.();

      setTimeout(() => {
        hideBtn();
      }, 800);
    } catch (error) {
      handleApiError(error, lang.PRODUCTS_DELETE_ERROR || 'Error deleting product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={handleTitle(mode)}
        content={
          <Container classNames={{ root: 'product-details' }}>
            <Grid>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={lang.HELPDESK_PRODUCT_CODE}
                  value={productCode}
                  onChange={e => setProductCode(e.target.value)}
                  disabled={mode === 'read' || mode === 'edit'}
                />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={lang.EQUIPMENT_DESCRIPTION}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  disabled={mode === 'read'}
                />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput label="UM" value={um} onChange={e => setUm(e.target.value)} disabled={mode === 'read'} />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={lang.PRICE_REFERENCE}
                  value={priceRef}
                  onChange={value => setPriceRef(value as number)}
                  disabled={mode === 'read'}
                  hideControls
                  suffix={` ${currency.currencyStr}`}
                />
              </GridCol>
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.LAST_SUPPLIER}
                    value={lastSupplier}
                    onChange={val => setLastSupplier(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                  />
                </GridCol>
              )}
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.LAST_PRICE}
                    value={lastPrice}
                    onChange={val => setLastPrice(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                    suffix={` ${currency.currencyStr}`}
                  />
                </GridCol>
              )}
              <GridCol span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={lang.AVERAGE_DELIVERY_TIME}
                  value={averDeliTime}
                  onChange={val => setAverDeliTime(val as number)}
                  disabled={mode === 'read'}
                  hideControls
                />
              </GridCol>
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.PRODUCT_DEV_TYPE}
                    value={devType}
                    onChange={val => setDevType(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                  />
                </GridCol>
              )}
              <GridCol span={{ base: 12 }}>
                <Group grow>
                  <Checkbox
                    label="BM"
                    checked={hasComposition}
                    onChange={e => setHasComposition(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.EQUIPMENT_SERIAL_NUMBER || 'Has Serial Number'}
                    checked={hasSn}
                    onChange={e => setHasSn(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.CE_CERTIFICATE || 'CE Certificate'}
                    checked={ceCertificate}
                    onChange={e => setCeCertificate(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.CB_CERTIFICATE || 'CB Certificate'}
                    checked={cbCertificate}
                    onChange={e => setCbCertificate(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                </Group>
              </GridCol>

              {(mode === 'edit' || mode === 'new') && (
                <Group className="buttons">
                  {mode === 'edit' && (
                    <>
                      <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                        {lang.GLOBAL_SAVE_BUTTON}
                      </Button>
                      <Button radius="md" type="button" className="delete" onClick={handleDelete} loading={loading}>
                        {lang.GLOBAL_DELETE}
                      </Button>
                    </>
                  )}

                  {mode === 'new' && (
                    <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                      {lang.GLOBAL_SAVE_BUTTON}
                    </Button>
                  )}
                </Group>
              )}
            </Grid>
          </Container>
        }
      />
    </>
  );
};

export default ProductModal;
